import Backbone from 'backbone'
import _ from 'lodash'
import dropdown from 'scoreboard/components/dropdown'
import FormView from 'scoreboard/components/form-view'
import i18n from 'scoreboard/components/i18n'
import './breakdown-by-field-type'
import breakdownByFieldType, { CATEGORY, RANGE } from './breakdown-by-field-type'

import SerieSelect from '../serie-select'
import SortByValueRangeSelect from './sort-by-value-range-select'
import directionText from './direction-text'
import template from './edit-sort-settings-view.hbs'
import * as sortLabels from './sort-labels'
import SortObjectTypes from './sort-object-types'
const { SERIE, BREAKDOWN1, BREAKDOWN2 } = SortObjectTypes
import ChartSortTypes from 'scoreboard/charts/model/chart-sort-types'
const { MANUAL, BY_LABEL, BY_VALUE } = ChartSortTypes

const EditSortSettingsView = Backbone.View.extend({
	className: 'edit-sort-settings-view',

	tagName: 'form',

	/**
	 * @type ChartDesignerSortSettings
	 */
	model: null,

	/**
	 * @type ChartDesigner
	 */
	chart: null,

	events: {
		'change [name=sortType]': 'onChangeSortType',
		'change [name=direction]': 'onChangeDirection',
		'change [name=serie]': 'onChangeSerie'
	},

	initialize: function (options) {
		this.options = options

		this.chart = options.chart
		this.sortObjectType = options.sortObjectType

		this.formView = new FormView({
			el: this.el,
			submit: this.done.bind(this)
		})
	},

	render() {
		this.$el.empty().append(template(this.context()))

		this.renderSortType()
		this.renderSerieSelect()
		this.renderBreakdown1RangeSelect()
		this.renderBreakdown2RangeSelect()
		this.renderDirection()
		this.renderDropdownVisibility()

		this.formView.start()

		return this
	},

	context: function () {
		let title = sortLabels.blankSorting({
			chart: this.chart,
			sortObjectType: this.sortObjectType
		})

		let sortTypeLabel = sortLabels.sortBlank({
			chart: this.chart,
			sortObjectType: this.sortObjectType
		})

		return { title, sortTypeLabel }
	},

	renderSortType: function () {
		let options = [
			{
				id: MANUAL,
				label: i18n.get('charts.sort.type.manual')
			},
			{
				id: BY_VALUE,
				label: 'charts.sort.type.byValue'
			},
			{
				id: BY_LABEL,
				label: sortLabels.sortByLabelText({
					sortObjectType: this.sortObjectType
				})
			}
		]

		dropdown.idLabels(this.sortTypeInput(), options, this.model.get('sortType'))
	},

	sortTypeInput: function () {
		return this.$('[name=sortType]')
	},

	onChangeSortType: function () {
		this.model.set('sortType', this.sortTypeInput().val())
		this.renderDirection()
		this.renderDropdownVisibility()
	},

	renderDirection: function () {
		let section = this.$('.direction-section')
		let select = this.directionInput().empty()

		let sortType = this.model.get('sortType')

		if (sortType === MANUAL) {
			return
		}

		let options = ['ascending', 'descending'].map(direction => {
			return {
				id: direction,
				label: directionText({ sortType, direction })
			}
		})

		dropdown.idLabels(select, options, this.model.get('direction'))
	},

	directionInput: function () {
		return this.$('[name=direction]')
	},

	onChangeDirection: function () {
		this.model.set('direction', this.directionInput().val())
	},

	renderSerieSelect: function () {
		if (this.sortObjectType === SERIE) {
			return
		}

		this.serieSourceSelect = SerieSelect.appendAndRender(
			this.$('.serie-section'),
			new SerieSelect({
				model: this.chart,
				name: 'serie',
				includeAll: true,
				selected: {
					id: this.model.get('featureId'),
					tempId: this.model.get('featureTempId')
				}
			}))
	},

	onChangeSerie: function () {
		let val = this.serieSourceSelect.val()
		this.model.set({
			featureId: val.id,
			featureTempId: val.tempId
		})
	},

	renderBreakdown1RangeSelect: function () {
		if (this.sortObjectType === BREAKDOWN1) {
			return
		}

		this.breakdown1Select = this.renderBreakdownSelect(1)
	},

	renderBreakdown2RangeSelect: function () {
		if (this.sortObjectType === BREAKDOWN2) {
			return
		}

		this.breakdown2Select = this.renderBreakdownSelect(2)
	},

	renderBreakdownSelect: function (breakdownNum) {
		let breakdown = this.chart.breakdowns.at(breakdownNum-1)
		if (!breakdown) {
			return
		}

		let section = this.$(`.breakdown-${breakdownNum}-range-section`)

		let select = new SortByValueRangeSelect({
			chart: this.chart,
			breakdown,
			sortByValueRange: this.initialSortByValueRange(breakdown),
			name: `breakdown${breakdownNum}SortByValueRange`
		})

		section.append(select.el)
		select.render()

		// Render label
		let breakdownBy = breakdown.get('breakdownBy')
		let label
		if (breakdownBy === 'calendar-period-ranges' || breakdownBy == 'calendar-period-grouped-ranges') {
			label = i18n.get('report.designer.periodForSorting')
		} else {
			let dataset = this.chart.data.datasetModel(breakdown.get('datasetId'))
			let field = dataset.getFieldByIds({
				fieldId: breakdown.get('datasetFieldId'),
				visibleLinkedFieldId: breakdown.get('datasetVisibleLinkedFieldId')
			})

			let breakdownType = breakdownByFieldType({ field, breakdownBy })
			let fieldOrCatLabel
			if (breakdownType == CATEGORY || breakdownType == RANGE) {
				let catOrRangeKey = 'datasets.breakdown.range.'
					+ (breakdownType === RANGE ? 'numeric' : 'text')
				fieldOrCatLabel = i18n.get(catOrRangeKey, field.label)
			} else {
				fieldOrCatLabel = field.label
			}

			label = i18n.get('report.designer.fieldNameForSorting', fieldOrCatLabel)
		}

		section.find('.label-text').text(label)
	},

	/**
	 * Returns the initial ChartSortByValueRange to use for the given breakdown's
	 * dropdown, setting a default if no intial value exists.
	 *
	 * @param {ChartDesignerBreakdown} breakdown - the breakdown for which a
	 * value/range/period/etc will be chosed
	 *
	 * @returns {ChartSortByValueRange}
	 */
	initialSortByValueRange: function (breakdown) {
		let sortRange
		if (this.sortObjectType === SERIE) {
			sortRange = this.model.findSortByValueRange( breakdown.pick('id', 'tempId'))

			if (!sortRange) {
				sortRange = newSortByValueRange(breakdown)

				let sortByValueRanges = this.model.get('sortByValueRanges')
				if (sortByValueRanges == null) {
					sortByValueRanges = []
					this.model.set('sortByValueRanges', sortByValueRanges)
				}

				sortByValueRanges.push(sortRange)
			}
		} else {
			sortRange = this.model.get('oppositeBreakdownSortByValueRange')

			if (!sortRange) {
				sortRange = newSortByValueRange(breakdown)
				this.model.set('oppositeBreakdownSortByValueRange', sortRange)
			}
		}

		return sortRange
	},

	renderDropdownVisibility: function () {
		let isManual = this.model.get('sortType') === MANUAL
		let isByLabel = this.model.get('sortType') === BY_LABEL

		// serie/range selectors are only valid for by-value, and when they
		// aren't the same object type being configured (eg. no serie selector
		// if we're doing sort settings for series)
		this.$('.serie-section').toggleClass('is-not-displayed',
			isManual || isByLabel || this.sortObjectType === SERIE)

		this.$('.breakdown-1-range-section').toggleClass('is-not-displayed',
			isManual || isByLabel || this.sortObjectType === BREAKDOWN1)

		this.$('.breakdown-2-range-section').toggleClass('is-not-displayed',
			isManual || isByLabel || this.sortObjectType === BREAKDOWN2)

		// Ascending/descending selector is always valid for non-manual sorting
		this.$('.direction-section').toggleClass('is-not-displayed', isManual)
	},

	done: function () {
		this.trigger('done')
	},

	remove() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.formView, this.breakdown1Select], 'remove')
	}
})

/**
 * Returns an initial ChartSortByValueRange for the given breakdown, set to All
 *
 * @param {ChartDesignerBreakdown} breakdown
 * @returns {ChartSortByValueRange}
 */
function newSortByValueRange (breakdown) {
	return {
		breakdownId: breakdown.get('id'),
		breakdownTempId: breakdown.get('tempId')
	}
}

export default EditSortSettingsView

EditSortSettingsView.__filename__ = 'charts/edit/sort/edit-sort-settings-view.js'
